// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class PropertyTagNfcsService {

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) { }

  /**
   * Create a new Tag NFC
   *
   * @since 1.0.0
   *
   * @param propertyId Property ID
   * @param data Tag NFC data
   * @returns Observable<any>
   */
  create(propertyId: string, data: any): Observable<any> {
    return this.apiService.apiCall('POST', `property-tag-nfcs/${propertyId}`, data);
  }

  /**
   * Get all Tag NFC list of property
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPropertyTagNFCList(propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `property-tag-nfcs/property/${propertyId}`, params);
  }
  
  /**
   * Get an exportation file of all Tag NFC of property
   *
   * @since 1.0.0
   */
  getExportPropertyComplete(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `property-tag-nfcs/property/export-complete/${propertyId}`, {  }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  /**
   * Get an exportation file of all Tag NFC of property
   *
   * @since 1.0.0
   */
  getExportPropertyShort(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `property-tag-nfcs/property/export-short/${propertyId}`, {  }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  
  /**
   * Update fields with given tagId
   *
   * @since 1.0.0
   *
   * @param tagId ID of property to update
   */
  updateFields(tagId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PATCH', `property-tag-nfcs/${tagId}`, data);
  }

  /**
   * Delete a Tag NFC.
   *
   * If the tag has already been used to record entrances/exits, the back-end
   * requires an admin to also provide `newTagNfcId`: the id of another Tag
   * NFC of the same property onto which those records get moved before the
   * deletion is performed.
   *
   * @since 1.0.0
   *
   * @param tagId ID of the Tag NFC to delete
   * @param [newTagNfcId] ID of the replacement Tag NFC of the same property
   */
  delete(tagId: string, newTagNfcId?: string): Observable<any> {
    const extraOptions = newTagNfcId ? { body: { newTagNfcId } } : undefined;
    return this.apiService.apiCall('DELETE', `property-tag-nfcs/${tagId}`, undefined, extraOptions);
  }
}
